// Offline lessons — download a lesson so it plays with no network at all,
// narration included.
//
// ── WHY THE BADGE IS COMPUTED, NOT STORED ────────────────────────────────
// The obvious design is a boolean per lesson in localStorage. It lies. The
// browser evicts Cache Storage under pressure — and iOS is the most eager
// evictor of the platforms this has to work on — so a stored `downloaded: true`
// survives long after the audio it refers to has gone. A learner would board a
// plane trusting a green badge and find silence.
//
// So `inspect()` asks the Cache what it ACTUALLY holds and compares it against
// the lesson's expected asset list. The badge only reads "available offline"
// when every expected URL is present. Slower by a few milliseconds, honest at
// 35,000 feet.
//
// ── WHY THE AUDIO IS FETCHED, NOT `cache.add()`-ED ───────────────────────
// Narration lives on R2 under a different origin. `cache.add()` would store an
// OPAQUE response: it plays back fine, but its status is 0 and its body length
// is hidden, so we could neither verify the download nor show real progress —
// and a failed fetch would be cached as a "success". We fetch explicitly and
// only put a response we have confirmed is `ok`.

import apiClient from '../../services/apiClient';
import { getAuthToken } from '../../stores/authStore';

export const CACHE_NAME = 'ace-offline-v1';

export interface LessonAssets {
  lessonId: string;
  /** Same-origin API responses (lesson body + audio manifest). */
  apiUrls: string[];
  /** Narration files, usually cross-origin on R2. */
  audioUrls: string[];
}

export interface OfflineState {
  status: 'absent' | 'partial' | 'ready';
  have: number;
  total: number;
  /** Bytes actually held in the cache, when the browser reports them. */
  bytes: number;
}

const abs = (path: string) => new URL(path, apiClient.defaults.baseURL || window.location.origin).toString();

/**
 * The cache key for an asset: origin + pathname, WITHOUT the query string.
 *
 * Narration URLs are pre-signed and expire hourly
 * (`routes/lessons.js` → `storage.signedUrl(key, { expiresIn: 3600 })`), so the
 * same audio file comes back under a DIFFERENT URL on every manifest fetch.
 * Keying the cache on the full URL would mean:
 *   • `inspect()` reports "not downloaded" an hour after downloading, because
 *     every signature differs — the badge would go dark on a plane;
 *   • re-downloading would store a second copy of every file rather than
 *     replacing it, quietly doubling the space used.
 *
 * So the signature is stripped for storage and lookup. The signature still does
 * its job — it is present on the network fetch that FILLS the cache, which is
 * the only moment authorisation is checked.
 */
const cacheKey = (url: string): string => {
  try { const u = new URL(url); return u.origin + u.pathname; }
  catch { return url; }
};

/** Everything a lesson needs to play with the network off. */
export async function collectAssets(lessonId: string): Promise<LessonAssets> {
  const apiUrls = [abs(`/api/v1/lessons/${lessonId}`), abs(`/api/v1/lessons/${lessonId}/audio`)];
  type AudioRow = { public_url?: string | null };
  const { data } = await apiClient.get<{ slides?: AudioRow[] }>(`/api/v1/lessons/${lessonId}/audio`);
  const audioUrls = (data?.slides ?? [])
    .map((row: AudioRow) => row.public_url)
    .filter((u): u is string => typeof u === 'string' && u.length > 0);
  return { lessonId, apiUrls, audioUrls };
}

/** What the cache genuinely holds right now. Never trust a stored flag. */
export async function inspect(assets: LessonAssets): Promise<OfflineState> {
  if (!('caches' in window)) return { status: 'absent', have: 0, total: 0, bytes: 0 };
  const cache = await caches.open(CACHE_NAME);
  const all = [...assets.apiUrls, ...assets.audioUrls];
  let have = 0;
  let bytes = 0;
  for (const url of all) {
    const hit = await cache.match(cacheKey(url), { ignoreVary: true });
    if (!hit) continue;
    have += 1;
    const len = Number(hit.headers.get('content-length') || 0);
    if (Number.isFinite(len)) bytes += len;
  }
  const total = all.length;
  return {
    status: total > 0 && have === total ? 'ready' : have === 0 ? 'absent' : 'partial',
    have,
    total,
    bytes,
  };
}

/**
 * Download every asset. Resolves only when all of them are stored, so a caller
 * can flip a badge on the promise without re-checking — though `inspect()`
 * remains the source of truth for anything shown later.
 */
export async function download(
  assets: LessonAssets,
  onProgress?: (done: number, total: number) => void,
): Promise<OfflineState> {
  if (!('caches' in window)) throw new Error('offline_unsupported');
  const cache = await caches.open(CACHE_NAME);

  // Ask the browser not to evict us. iOS in particular treats an installed,
  // persisted PWA far more kindly than a casual tab. Best-effort: a refusal is
  // not a reason to abandon the download.
  try { await navigator.storage?.persist?.(); } catch { /* not fatal */ }

  const all = [...assets.apiUrls, ...assets.audioUrls];
  let done = 0;
  onProgress?.(0, all.length);

  for (const url of all) {
    const isApi = assets.apiUrls.includes(url);
    // Our own API needs the bearer token apiClient's interceptor normally adds;
    // a bare fetch inherits nothing. R2 is signed in the URL itself and rejects
    // credentialed requests, so it gets plain CORS and no headers.
    const token = isApi ? getAuthToken() : null;
    const res = await fetch(url, isApi
      ? { mode: 'cors', headers: token ? { Authorization: `Bearer ${token}` } : {} }
      : { mode: 'cors' });
    if (!res.ok) throw new Error(`download_failed:${res.status}`);
    await cache.put(cacheKey(url), res.clone());
    done += 1;
    onProgress?.(done, all.length);
  }

  return inspect(assets);
}

/** Free the space again. */
export async function remove(assets: LessonAssets): Promise<void> {
  if (!('caches' in window)) return;
  const cache = await caches.open(CACHE_NAME);
  await Promise.all([...assets.apiUrls, ...assets.audioUrls].map((u) => cache.delete(cacheKey(u), { ignoreVary: true })));
}

/**
 * How much space this lesson will take, asked of the server rather than guessed.
 *
 * Narration dominates: slide JSON is kilobytes, audio is megabytes. A HEAD per
 * file is cheap and gives a real number, which matters because the failure we
 * are guarding against is silent — a browser that hits its quota mid-download
 * throws a QuotaExceededError on `cache.put`, leaving a PARTIALLY downloaded
 * lesson that looks fine until the slide with no audio.
 *
 * Falls back to null (unknown) rather than a fabricated estimate if the server
 * does not return Content-Length; callers must treat null as "proceed, but you
 * cannot promise it will fit".
 */
export async function estimateBytes(assets: LessonAssets): Promise<number | null> {
  let total = 0;
  let known = 0;
  for (const url of assets.audioUrls) {
    try {
      const res = await fetch(url, { method: 'HEAD', mode: 'cors' });
      const len = Number(res.headers.get('content-length') || 0);
      if (len > 0) { total += len; known += 1; }
    } catch { /* unreachable file — counted as unknown */ }
  }
  if (known === 0) return null;
  // Scale up if some files did not report a length, so the estimate errs high.
  return Math.round(total * (assets.audioUrls.length / known));
}

/**
 * Will it fit? Returns the shortfall in bytes when it will not.
 *
 * A 20% headroom margin is kept: browsers begin evicting well before the
 * reported quota, and filling storage to the brim is how you lose the OTHER
 * lessons already downloaded.
 */
export async function checkFits(bytes: number | null): Promise<{ fits: boolean; shortfallBytes: number; quotaBytes: number; usedBytes: number }> {
  const est = await navigator.storage?.estimate?.();
  const quota = est?.quota ?? 0;
  const used = est?.usage ?? 0;
  if (!quota || bytes == null) return { fits: true, shortfallBytes: 0, quotaBytes: quota, usedBytes: used };
  const usable = quota * 0.8;
  const free = usable - used;
  return {
    fits: bytes <= free,
    shortfallBytes: Math.max(0, Math.round(bytes - free)),
    quotaBytes: quota,
    usedBytes: used,
  };
}

/** Rough size of everything downloaded, for a settings screen. */
export async function usage(): Promise<{ usedBytes: number; quotaBytes: number }> {
  const est = await navigator.storage?.estimate?.();
  return { usedBytes: est?.usage ?? 0, quotaBytes: est?.quota ?? 0 };
}
